<template>
  <v-card>
    <v-form ref="form" v-model="valid" :lazy-validation="true">
      <v-card-title>{{ title }}</v-card-title>
      <v-card-actions>
        <v-icon class="close" @click="$emit('close')">$sl-close</v-icon>
      </v-card-actions>

      <v-card-text class="mt-n2">
        <template v-if="mode === 'show'">
          <p class="overline">Yrke</p>
          <p class="mt-n3">{{ tc.profession.name }}</p>
          <template v-if="personMachineQualifications">
            <p class="overline">Maskinslag</p>
            <p class="mt-n3">
              {{ personMachineQualifications }}
            </p>
          </template>
          <p class="overline">Utfärdat</p>
          <p class="mt-n3">{{ tc.issuedOn | date }}</p>
          <p class="overline">Utfärdat av</p>
          <p class="mt-n3">
            {{
              tc.createdById ? tc.createdBy.firstName + " " + tc.createdBy.lastName : "-"
            }}
          </p>
          <p class="overline">Region</p>
          <p class="mt-n3">{{ tc.region.name }}</p>
          <template v-if="tc.approvedOn">
            <p class="overline">Godkänt</p>
            <p class="mt-n3">{{ tc.approvedOn | date }}</p>
            <p class="overline">Godkänt av</p>
            <p class="mt-n3">
              {{
                tc.approvedBy
                  ? tc.approvedBy.firstName + " " + tc.approvedBy.lastName
                  : "-"
              }}
            </p>
          </template>
          <template v-else>
            <p class="overline mt-3">Godkänd av</p>
            <p class="mt-n3 grey-text font-italic">Ej godkänd.</p>
          </template>
        </template>

        <template v-else-if="mode === 'approve'">
          <p class="overline">Yrke</p>
          <p class="mt-n3">{{ tc.profession.name }}</p>
          <template v-if="personMachineQualifications">
            <p class="overline">Maskinslag</p>
            <p class="mt-n3">
              {{ personMachineQualifications }}
            </p>
          </template>
          <p class="overline">Utfärdat</p>
          <p class="mt-n3">{{ tc.issuedOn | date }}</p>
          <p class="overline">Utfärdat av</p>
          <p class="mt-n3">
            {{
              tc.createdById ? tc.createdBy.firstName + " " + tc.createdBy.lastName : "-"
            }}
          </p>
          <p class="overline">Region</p>
          <p class="mt-n3">{{ tc.region.name }}</p>
        </template>

        <template v-if="mode === 'create'">
          <v-select
            v-model="orderData.professionId"
            v-on:change="resetMachineItems"
            :items="
              basicProfessions.map((o) => {
                return { id: o.id, text: o.name };
              })
            "
            item-text="text"
            outlined
            item-value="id"
            label="Yrke"
            data-cy="selectProfession"
            :rules="[rules.required]"
          ></v-select>

          <v-select
            v-if="isMachinesProfession"
            outlined
            multiple
            v-model="orderData.machineIds"
            :items="professionMachines"
            item-text="text"
            item-value="id"
            label="Maskinslag"
            data-cy="selectMachineProfession"
            :rules="[rules.notEmpty]"
          ></v-select>

          <v-select
            outlined
            v-model="orderData.regionId"
            :items="selectableRegions"
            item-text="text"
            item-value="id"
            label="Region"
            :rules="[rules.required]"
            append-icon="$sl-arrow-down"
            data-cy="selectRegion"
          ></v-select>

          <DatePicker
            :icon="'$sl-calendar-date'"
            :label="'Utfärdandedatum'"
            :width="'250'"
            outlined
            v-model="orderData.issuedOn"
            ref="datePicker"
            data-cy="addDate"
          />
        </template>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="secondary" @click="$emit('close')">
          <v-icon>$sl-cancel</v-icon>Avbryt
        </v-btn>
        <v-btn v-if="userAdminAndKanslistCan()" outlined @click="deleteCertificate(tc)">
          <v-icon>$sl-delete</v-icon>Radera
        </v-btn>
        <v-btn v-if="mode === 'create'" @click="createCertificate" :disabled="!canCreate">
          <v-icon>$sl-check</v-icon>Utfärda
        </v-btn>
        <v-btn v-if="mode === 'approve'" @click="$emit('approve', tc)" :disabled="!canApprove">
          <v-icon>$sl-like</v-icon>Godkänn
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import DatePicker from "@/components/common/DatePicker";
import { Regions, RulesMixin, UserRules, CanIDo } from "@/components/mixins";
import get from "lodash/get";
import API from "@/services/api";
// @group Person
// Styled PersonTradeCertificateModal component
export default {
  name: "PersonTradeCertificateModal",
  mixins: [Regions, RulesMixin, UserRules, CanIDo],
  components: {
    DatePicker,
  },

  data: () => ({
    valid: true,
    orderData: {
      personId: null,
      regionId: null,
      professionId: null,
      issuedOn: null,
      name: null,
      attention: null,
      address: null,
      address2: null,
      zip: null,
      city: null,
      machineIds: [],
    },
  }),
  props: {
    // trade certificate object
    tc: Object,
    // modal mode string
    mode: String,
    //has title
    title: {
      // `String`
      type: String,
      // The default value is: `Yrkesbevis`
      default: "Yrkesbevis",
    },
  },

  computed: {
    ...mapGetters("currentuser", ["currentUser", "currentUserRole"]),
    ...mapGetters("person", ["selectedPerson"]),
    ...mapGetters("basic", ["basicRegions", "basicProfessions", "basicMachines"]),
    professionMachines() {
      let ret = [];
      const selectedProfession = this.basicProfessions.find(
        (p) => p.id === this.orderData.professionId
      );

      if (selectedProfession && selectedProfession.machineOrientations) {
        ret = selectedProfession.machineOrientations.map((o) => {
          return { id: o.machine.id, text: o.name };
        });
      }
      return ret;
    },
    isMachinesProfession() {
      if (!this.orderData.professionId) return false;
      const selectedProfession = this.basicProfessions.find(
        (p) => p.id === this.orderData.professionId
      );
      return selectedProfession.isMachineProfession;
    },
    personMachineQualifications() {
      if (!this.tc || !this.tc.id) return [];
      const machineQualifications = get(this, "selectedPerson.machinequalifications", []);
      const certificateMachineQualifications = machineQualifications
        .filter((mc) => mc.tradeCertificateId === this.tc.id)
        .reduce((acc, mc) => {
          return acc + mc.machine.name + ", ";
        }, "")
        .slice(0, -2);
      return certificateMachineQualifications;
    },
  },

  asyncComputed: {
    canApprove(){
      return this.canIDo('person-qualification', 'update');
    },
    canCreate(){
      return this.canIDo('person-qualification', 'create');
    }
  },

  methods: {
    ...mapActions("person", ["setSelectedPerson"]),
    resetMachineItems() {
      this.orderData.machineIds = [];
    },

    async createCertificate() {
      if (
        !this.$refs.form.validate() ||
        !this.$refs.datePicker.$refs.form.validate() ||
        (this.orderData &&
          (this.orderData.professionId == null ||
            this.orderData.issuedOn == null ||
            this.orderData.regionId == null))
      ) {
        return this.$events.fire("showError", "Formuläret är inte korrekt ifyllt.");
      }
      /**
       * @vuese
       * Used to create trade certificate
       * @arg The argument is object values representing trade certificate
       */

      this.orderData.personId = this.selectedPerson.id;

      const res = await this.$dialog.confirm({
        text: `Yrkesbevis för ${this.selectedPerson.firstName} ${this.selectedPerson.lastName}.`,
        title: "Vill du verkligen utfärda yrkesbevis?",
        actions: [
          { text: "Avbryt", key: false, icon: "$sl-cancel", outlined: true },
          { text: "Utfärda", key: true, icon: "$sl-check" },
        ],
      });
      if (res !== true) return;

      API.createTradeCertificate({ data: this.orderData })
        .then((res) => {
          /**
           * @vuese
           * Used to fetch person
           * @arg The argument is integer values representing person id
           */
          this.$refs.form.reset();
          this.$refs.datePicker.$refs.form.reset();
          API.getPerson(this.selectedPerson.id)
            .then((response) => {
              /**
               * @vuese
               * Used to update local store with the person data
               * @arg the argument is an object representing person
               */
              this.setSelectedPerson(response.data);
              this.$emit("close");
            })
            .catch((error) => {
              console.log("There was an error:", error.response);
            });

          return res.data.id;
        })
          .then((id) => {
            API.get
          })
        .catch((error) => {
          console.log(error);
        });
    },
    async deleteCertificate(tc) {
      const res = await this.$dialog.confirm({
        text: "Radering av yrkesbevis går inte att ångra.",
        title: `Vill du radera yrkesbevis för ${this.selectedPerson.firstName} ${this.selectedPerson.lastName}?`,
        actions: [
          { text: "Radera", icon: "$sl-delete", key: true },
          { text: "Avbryt", key: false, icon: "$sl-cancel", outlined: true },
        ],
      });
      if (res !== true || (!tc && !tc.id)) return;

      API.deleteTradeCertificate(tc.id)
        .then((res) => {
          // this.selectedPerson.tradeCertificates.splice(
          //   this.selectedPerson.tradeCertificates.indexOf(tc),
          //   1
          // );
          // this.selectedPerson.tradeCertificates = [
          //   ...this.selectedPerson.tradeCertificates,
          // ];
          API.getPerson(this.selectedPerson.id)
            .then((response) => {
              /**
               * @vuese
               * Used to update local store with the person data
               * @arg the argument is an object representing person
               */
              this.setSelectedPerson(response.data);
              this.$events.fire("showInfo", "Raderad.");
              this.$emit("close");
            })
            .catch((error) => {
              console.log("There was an error:", error.response);
            });
          // this.$events.fire("showInfo", "Raderad.");
          // this.$emit("close");
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
};
</script>

<style></style>
